import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import axios from "axios";
import {
  disableAllAssessmentsContext,
  initializeAllAssessmentsContextHeader,
} from "modules/utils/allAssessmentsContext";

interface User {
  displayName: string;
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  userGuid: string;
}

interface Organization {
  organizationId: string;
  organizationName: string;
  userRoles: string[];
}

interface Token {
  token: string;
  expiry: number;
  redirectURI: string;
}

interface AuthState {
  token: Token;
  user: User;
  roles: string[];
  organizations: Organization[];
  canAccessAllAssessments: boolean;
  canAccessP3M3OnlyAllAssessments: boolean;
}

interface AuthContextData {
  user: User;
  tokenExpireDate: number;
  roles: string[];
  rolesForSelectedOrganization: string[];
  organizations: Organization[];
  canAccessAllAssessments: boolean;
  canAccessP3M3OnlyAllAssessments: boolean;
  selectedOrganizationId: string | null;
  setSelectedOrganizationId: (organizationId: string) => void;
  updateUser(user: User): void;
  fetchAuth(token: string): Promise<boolean>;
  fetchRefreshToken: () => Promise<void>;
  logout: () => string;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const MATURITY_MODELS = ["P3M3", "ITIL"] as const;
type MaturityModel = typeof MATURITY_MODELS[number];
const SELECTED_ORG_HEADER = "X-Selected-Organization-Id";
const CAN_ACCESS_ALL_ASSESSMENTS_KEY = "@P3M3:canAccessAllAssessments";
const CAN_ACCESS_P3M3_ONLY_ALL_ASSESSMENTS_KEY =
  "@P3M3:canAccessP3M3OnlyAllAssessments";

function readCanAccessAllAssessments(): boolean {
  const stored = sessionStorage.getItem(CAN_ACCESS_ALL_ASSESSMENTS_KEY);
  return stored === "true";
}

function readCanAccessP3M3OnlyAllAssessments(): boolean {
  const stored = sessionStorage.getItem(
    CAN_ACCESS_P3M3_ONLY_ALL_ASSESSMENTS_KEY
  );
  return stored === "true";
}

function normalizeMaturityModelRoles(
  roles: unknown,
  organizations: Organization[] | undefined
): string[] {
  const out = new Set<string>();

  if (Array.isArray(roles)) {
    for (const r of roles) {
      const s = String(r);
      if (MATURITY_MODELS.includes(s as MaturityModel)) {
        out.add(s);
      } else {
        const upper = s.toUpperCase();
        if (upper.includes("P3M3")) out.add("P3M3");
        if (upper.includes("ITIL")) out.add("ITIL");
      }
    }
  }

  // If the API didn't return roles, infer maturity model access from organizations[].userRoles.
  if (out.size === 0 && organizations && organizations.length > 0) {
    for (const org of organizations) {
      for (const userRole of org.userRoles || []) {
        const upper = String(userRole).toUpperCase();
        if (upper.includes("P3M3")) out.add("P3M3");
        if (upper.includes("ITIL")) out.add("ITIL");
      }
    }
  }

  return Array.from(out);
}

function getRolesForOrganization(
  organizations: Organization[] | undefined,
  organizationId: string | null
): string[] {
  const out = new Set<string>();

  if (!organizations || !organizationId) {
    return [];
  }

  // Find the selected organization by matching organizationId
  const selectedOrg = organizations.find(
    (org) => org.organizationId === organizationId
  );

  if (!selectedOrg || !selectedOrg.userRoles) {
    return [];
  }

  // Extract maturity models from user roles for the selected organization only
  for (const userRole of selectedOrg.userRoles) {
    const upper = String(userRole).toUpperCase();
    if (upper.includes("P3M3")) out.add("P3M3");
    if (upper.includes("ITIL")) out.add("ITIL");
  }

  return Array.from(out);
}

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = sessionStorage.getItem("@P3M3:token");
    const refreshToken = sessionStorage.getItem("@P3M3:refreshToken");
    const user = sessionStorage.getItem("@P3M3:user");
    const roles = sessionStorage.getItem("@P3M3:roles");
    const organizations = sessionStorage.getItem("@P3M3:organizations");
    const canAccessAllAssessments = readCanAccessAllAssessments();
    const canAccessP3M3OnlyAllAssessments =
      readCanAccessP3M3OnlyAllAssessments();

    if (refreshToken && user && roles) {
      const parsedToken = JSON.parse(refreshToken);
      const parsedOrganizations = organizations
        ? JSON.parse(organizations)
        : [];
      const parsedRoles = normalizeMaturityModelRoles(
        JSON.parse(roles),
        parsedOrganizations
      );

      const tokenDate = new Date(parsedToken.expiry * 1000);
      const currentDate = new Date();

      if (tokenDate >= currentDate) {
        axios.defaults.headers.authorization = `Bearer ${parsedToken.token}`;
        return {
          token: parsedToken,
          user: JSON.parse(user),
          roles: parsedRoles,
          organizations: parsedOrganizations,
          canAccessAllAssessments,
          canAccessP3M3OnlyAllAssessments,
        };
      }
    }

    if (token && user && roles) {
      const parsedToken = JSON.parse(token);
      const parsedOrganizations = organizations
        ? JSON.parse(organizations)
        : [];
      const parsedRoles = normalizeMaturityModelRoles(
        JSON.parse(roles),
        parsedOrganizations
      );

      const tokenDate = new Date(parsedToken.expiry * 1000);
      const currentDate = new Date();

      if (tokenDate >= currentDate) {
        axios.defaults.headers.authorization = `Bearer ${parsedToken.token}`;
        return {
          token: parsedToken,
          user: JSON.parse(user),
          roles: parsedRoles,
          organizations: parsedOrganizations,
          canAccessAllAssessments,
          canAccessP3M3OnlyAllAssessments,
        };
      }
    }

    return {} as AuthState;
  });

  useEffect(() => {
    initializeAllAssessmentsContextHeader();
  }, []);

  useEffect(() => {
    const tokenExpireDate = data?.token?.expiry;
    const currentDate = Date.now();
    const offsetTimeout = 1000 * 60 * 5;

    const intervalTime = tokenExpireDate
      ? tokenExpireDate * 1000 - currentDate
      : 1000 * 60 * 25;

    if (intervalTime <= offsetTimeout) {
      fetchRefreshToken();
      return;
    }

    const timer = setTimeout(
      () => {
        fetchRefreshToken();
      },
      tokenExpireDate ? intervalTime - offsetTimeout : intervalTime
    );

    return () => {
      clearTimeout(timer);
    };
  }, [data]);

  const fetchAuth = async (tokenParam: string) => {
    const AUTHHOST = process.env.REACT_APP_API_ROOT;

    try {
      const response = await axios
        .post(
          `${AUTHHOST}/auth`,
          { token: tokenParam },
          {
            withCredentials: true,
          }
        )
        .then((res) => res.data);

      if (response) {
        const {
          expiry,
          token,
          user,
          redirectURI,
          roles,
          organizations,
          canAccessAllAssessments,
          canAccessP3M3OnlyAllAssessments,
        } = response;

        const responseToken: Token = {
          expiry,
          token,
          redirectURI,
        };

        if (user && token && roles) {
          const normalizedRoles = normalizeMaturityModelRoles(
            roles,
            organizations || []
          );
          sessionStorage.setItem("@P3M3:token", JSON.stringify(responseToken));
          sessionStorage.setItem("@P3M3:user", JSON.stringify(user));
          sessionStorage.setItem(
            "@P3M3:roles",
            JSON.stringify(normalizedRoles)
          );
          sessionStorage.setItem(
            "@P3M3:organizations",
            JSON.stringify(organizations || [])
          );
          sessionStorage.setItem(
            CAN_ACCESS_ALL_ASSESSMENTS_KEY,
            String(Boolean(canAccessAllAssessments))
          );
          sessionStorage.setItem(
            CAN_ACCESS_P3M3_ONLY_ALL_ASSESSMENTS_KEY,
            String(Boolean(canAccessP3M3OnlyAllAssessments))
          );

          axios.defaults.headers.authorization = `Bearer ${token}`;

          setData({
            token: responseToken,
            user,
            roles: normalizedRoles,
            organizations: organizations || [],
            canAccessAllAssessments: Boolean(canAccessAllAssessments),
            canAccessP3M3OnlyAllAssessments: Boolean(
              canAccessP3M3OnlyAllAssessments
            ),
          });
        }

        return true;
      }
    } catch (error) {
      return false;
    }

    return false;
  };

  const fetchRefreshToken = async () => {
    const AUTHHOST = process.env.REACT_APP_API_ROOT;

    try {
      await axios
        .post(
          `${AUTHHOST}/auth/refresh`,
          {},
          {
            withCredentials: true,
          }
        )
        .then((response) => {
          const {
            expiry,
            token,
            user,
            redirectURI,
            organizations,
            canAccessAllAssessments,
            canAccessP3M3OnlyAllAssessments,
          } = response.data;

          if (user && token) {
            const responseToken: Token = {
              expiry,
              token,
              redirectURI,
            };

            sessionStorage.setItem(
              "@P3M3:refreshToken",
              JSON.stringify(responseToken)
            );
            axios.defaults.headers.authorization = `Bearer ${token}`;

            // Temp - Needs to add roles on refresh endpoint
            const roles = sessionStorage.getItem("@P3M3:roles");
            const parsedRolesRaw = roles ? JSON.parse(roles) : [];
            // Some environments don't return `organizations` from /auth/refresh.
            // Preserve whatever we already have (from /auth or sessionStorage) instead of wiping it out.
            const storedOrganizationsRaw = sessionStorage.getItem(
              "@P3M3:organizations"
            );
            const storedOrganizations = storedOrganizationsRaw
              ? JSON.parse(storedOrganizationsRaw)
              : [];
            const parsedOrganizations =
              typeof organizations !== "undefined"
                ? organizations || []
                : storedOrganizations;
            const parsedRoles = normalizeMaturityModelRoles(
              parsedRolesRaw,
              parsedOrganizations
            );

            sessionStorage.setItem(
              "@P3M3:organizations",
              JSON.stringify(parsedOrganizations)
            );
            sessionStorage.setItem("@P3M3:roles", JSON.stringify(parsedRoles));

            const canAccessAllAssessmentsValue =
              typeof canAccessAllAssessments !== "undefined"
                ? Boolean(canAccessAllAssessments)
                : readCanAccessAllAssessments();
            const canAccessP3M3OnlyAllAssessmentsValue =
              typeof canAccessP3M3OnlyAllAssessments !== "undefined"
                ? Boolean(canAccessP3M3OnlyAllAssessments)
                : readCanAccessP3M3OnlyAllAssessments();
            sessionStorage.setItem(
              CAN_ACCESS_ALL_ASSESSMENTS_KEY,
              String(canAccessAllAssessmentsValue)
            );
            sessionStorage.setItem(
              CAN_ACCESS_P3M3_ONLY_ALL_ASSESSMENTS_KEY,
              String(canAccessP3M3OnlyAllAssessmentsValue)
            );

            setData({
              user,
              token: responseToken,
              roles: parsedRoles,
              organizations: parsedOrganizations,
              canAccessAllAssessments: canAccessAllAssessmentsValue,
              canAccessP3M3OnlyAllAssessments:
                canAccessP3M3OnlyAllAssessmentsValue,
            });
          }
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Error trying to get RefreshToken");
    }
  };

  const logout = useCallback(() => {
    sessionStorage.removeItem("@P3M3:token");
    sessionStorage.removeItem("@P3M3:refreshToken");
    sessionStorage.removeItem("@P3M3:user");
    sessionStorage.removeItem("@P3M3:roles");
    sessionStorage.removeItem("@P3M3:organizations");
    sessionStorage.removeItem(CAN_ACCESS_ALL_ASSESSMENTS_KEY);
    sessionStorage.removeItem(CAN_ACCESS_P3M3_ONLY_ALL_ASSESSMENTS_KEY);
    disableAllAssessmentsContext();
    localStorage.removeItem("@P3M3:selectedOrganizationId");
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete axios.defaults.headers.common[SELECTED_ORG_HEADER];

    if (!data.token.redirectURI) {
      return "https://www.peoplecert.org/";
    }

    return data.token.redirectURI.includes("http")
      ? data.token.redirectURI
      : `https://${data.token.redirectURI}`;
  }, [data.token]);

  const updateUser = useCallback(
    (user: User) => {
      sessionStorage.setItem("@P3M3:user", JSON.stringify(user));

      setData({
        token: data.token,
        user,
        roles: data.roles,
        organizations: data.organizations,
        canAccessAllAssessments: data.canAccessAllAssessments,
        canAccessP3M3OnlyAllAssessments: data.canAccessP3M3OnlyAllAssessments,
      });
    },
    [
      data.token,
      data.roles,
      data.organizations,
      data.canAccessAllAssessments,
      data.canAccessP3M3OnlyAllAssessments,
    ]
  );

  // Initialize selected organization from localStorage or default to first organization
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<
    string | null
  >(() => {
    const stored = localStorage.getItem("@P3M3:selectedOrganizationId");
    if (stored) {
      axios.defaults.headers.common[SELECTED_ORG_HEADER] = stored;
    }
    return stored || null;
  });

  // Update selected organization when organizations change
  useEffect(() => {
    if (data.organizations && data.organizations.length > 0) {
      const current = localStorage.getItem("@P3M3:selectedOrganizationId");
      if (
        !current ||
        !data.organizations.find((org) => org.organizationId === current)
      ) {
        // If no selection or selected org is not in list, select first one
        const firstOrgId = data.organizations[0].organizationId;
        setSelectedOrganizationIdState(firstOrgId);
        localStorage.setItem("@P3M3:selectedOrganizationId", firstOrgId);
      }
    }
  }, [data.organizations]);

  const setSelectedOrganizationId = useCallback((organizationId: string) => {
    setSelectedOrganizationIdState(organizationId);
    localStorage.setItem("@P3M3:selectedOrganizationId", organizationId);
    axios.defaults.headers.common[SELECTED_ORG_HEADER] = organizationId;
  }, []);

  useEffect(() => {
    if (selectedOrganizationId) {
      axios.defaults.headers.common[SELECTED_ORG_HEADER] =
        selectedOrganizationId;
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete axios.defaults.headers.common[SELECTED_ORG_HEADER];
  }, [selectedOrganizationId]);

  // Ensure selectedOrganizationId is set from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("@P3M3:selectedOrganizationId");
    if (stored && stored !== selectedOrganizationId) {
      setSelectedOrganizationIdState(stored);
    } else if (!stored && data.organizations && data.organizations.length > 0) {
      const firstOrgId = data.organizations[0].organizationId;
      setSelectedOrganizationIdState(firstOrgId);
      localStorage.setItem("@P3M3:selectedOrganizationId", firstOrgId);
    }
  }, []);

  const currentSelectedOrgId =
    selectedOrganizationId ||
    (data.organizations && data.organizations.length > 0
      ? data.organizations[0].organizationId
      : null);

  const rolesForSelectedOrg = getRolesForOrganization(
    data.organizations,
    currentSelectedOrgId
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        tokenExpireDate: data?.token?.expiry ? data.token.expiry : 0,
        roles: normalizeMaturityModelRoles(data.roles, data.organizations),
        rolesForSelectedOrganization: rolesForSelectedOrg,
        organizations: data.organizations || [],
        canAccessAllAssessments: Boolean(data.canAccessAllAssessments),
        canAccessP3M3OnlyAllAssessments: Boolean(
          data.canAccessP3M3OnlyAllAssessments
        ),
        selectedOrganizationId: currentSelectedOrgId,
        setSelectedOrganizationId,
        updateUser,
        fetchAuth,
        fetchRefreshToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export { AuthProvider, useAuth };
